import * as Yup from 'yup'

import { formatNumber } from '../../../../../helpers/numbers'
import { getNumberOfDays } from '../../../../../helpers/date'
import { calculateBudgetFromImpressions } from '../../../../Multiplatform/UploadAdCreative/ImpressionsBudgetSection/helpers'
import { formatCampaignName } from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeCampaignForm/Steps/CampaignNameStep/CampaignNameFields/helpers'

import {
  TARGET_ROAS,
  BUDGET_DAILY,
  CAMPAIGN_BUDGET,
  NAME,
  AMOUNT_MICROS,
  MAXIMIZE_CONVERSION_VALUE
} from '../../../../Multiplatform/CampaignForms/fields'
import {
  defaultGoogleCampaignPayload,
  PERFORMANCE_MAX_TYPE,
  DISPLAY_TYPE
} from '../../../CampaignForms/CampaignGoogleCreate/CampaignGoogleCreateForm/fields'
import {
  campaignValidation,
  getCampaignInitialValues
} from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeCampaignForm/fields'
import { DIGITAL_ALLOCATION_TYPE_IMPRESSIONS } from '../../../../Multiplatform/Partnerships/RightForms/fields'
import {
  START_DATE,
  END_DATE,
  startEndDateRangeInitialValues,
  startDateValidation,
  endDateValidation
} from '../../../../ReusableFormFields/StartEndDateRangeFields/fields'
import { formatDateBE } from '../../../../../constants/dates'
import { LIST_ITEM_OPTION, OPTION_NEW_LIST_ITEM } from '../../../../../hooks/formHooks/listItemFormHooks/fields'

export const CAMPAIGN_TYPE = 'advertising_channel_type'

export const BUDGET_LIFETIME = 'budget_lifetime'

export const IMPRESSIONS = 'impressions'

export const getInitialValues = (isCampaignsExist, allocationType) => {
  const initialCampaignValues = getCampaignInitialValues(isCampaignsExist)

  return {
    // campaign
    // If allocation_unit = impressions, do not give the user an option to create performance max - default to display
    [CAMPAIGN_TYPE]: allocationType === DIGITAL_ALLOCATION_TYPE_IMPRESSIONS ? DISPLAY_TYPE : PERFORMANCE_MAX_TYPE,
    ...initialCampaignValues,
    [BUDGET_DAILY]: '',
    [TARGET_ROAS]: '',
    ...startEndDateRangeInitialValues
  }
}

export const getMaxDailyImpressions = ({
  remainingImpressions,
  startDate,
  endDate,
  initialCampaignImpressionsDaily = 0
}) => {
  const days = getNumberOfDays(startDate, endDate)

  const calculatedInitialCampaignImpressionsLifetime = initialCampaignImpressionsDaily * days

  const maxImpressionsLifetime = remainingImpressions + calculatedInitialCampaignImpressionsLifetime
  const maxImpressionsDaily = maxImpressionsLifetime / days

  return maxImpressionsDaily
}

export const getDailyImpressionsValidation = ({
  remainingImpressions,
  value,
  context,
  startDate,
  endDate,
  initialCampaignImpressionsDaily = 0
}) => {
  const maxDailyImpressions = getMaxDailyImpressions({
    remainingImpressions,
    startDate,
    endDate,
    initialCampaignImpressionsDaily
  })

  if (!value) {
    return context.createError({
      message: 'Daily impressions is required',
      path: IMPRESSIONS
    })
  }

  if (value > maxDailyImpressions) {
    return context.createError({
      message: 'Max daily impressions is ' + formatNumber(maxDailyImpressions, { max: 2 }),
      path: IMPRESSIONS
    })
  }

  return true
}

const getCreateCampaignRemainingDailyImpressionsValidation = remainingImpressions => {
  return Yup.number().test({
    name: 'Daily impressions validation based on number of days',

    test: (value, context) => {
      const startDate = context.parent[START_DATE]
      const endDate = context.parent[END_DATE]

      return getDailyImpressionsValidation({
        remainingImpressions,
        value,
        context,
        startDate,
        endDate
      })
    }
  })
}

export const getMaxDailyBudget = ({ remainingBudget, startDate, endDate, initialCampaignBudgetDaily = 0 }) => {
  const days = getNumberOfDays(startDate, endDate)

  const calculatedInitialCampaignBudgetLifetime = initialCampaignBudgetDaily * days

  const maxBudgetLifetime = remainingBudget + calculatedInitialCampaignBudgetLifetime
  const maxBudgetDaily = maxBudgetLifetime / days

  return maxBudgetDaily
}

export const getDailyBudgetValidation = ({
  remainingBudget,
  value,
  context,
  startDate,
  endDate,
  initialCampaignBudgetDaily
}) => {
  const maxBudgetDaily = getMaxDailyBudget({
    remainingBudget,
    startDate,
    endDate,
    initialCampaignBudgetDaily
  })

  if (!value) {
    return context.createError({
      message: 'Daily budget is required',
      path: BUDGET_DAILY
    })
  }

  if (value > maxBudgetDaily) {
    return context.createError({
      message: 'Max daily budget is ' + formatNumber(maxBudgetDaily, { max: 2 }),
      path: BUDGET_DAILY
    })
  }

  return true
}

const getCreateCampaignRemainingDailyBudgetValidation = remainingBudget => {
  return Yup.number().test({
    name: 'Daily budget validation based on number of days',

    test: (value, context) => {
      const startDate = context.parent[START_DATE]
      const endDate = context.parent[END_DATE]
      return getDailyBudgetValidation({
        remainingBudget,
        value,
        context,
        startDate,
        endDate
      })
    }
  })
}

export const getValidationSchema = ({ showImpressions, remainingImpressions, remainingBudget }) => {
  return Yup.object({
    ...campaignValidation,
    [START_DATE]: Yup.string().when(LIST_ITEM_OPTION, {
      is: OPTION_NEW_LIST_ITEM,
      then: () => startDateValidation
    }),
    [END_DATE]: Yup.string().when(LIST_ITEM_OPTION, {
      is: OPTION_NEW_LIST_ITEM,
      then: () => endDateValidation
    }),
    ...(showImpressions
      ? {
          [IMPRESSIONS]: Yup.number().when(LIST_ITEM_OPTION, {
            is: OPTION_NEW_LIST_ITEM,
            then: () => getCreateCampaignRemainingDailyImpressionsValidation(remainingImpressions)
          })
        }
      : {
          [BUDGET_DAILY]: Yup.number().when(LIST_ITEM_OPTION, {
            is: OPTION_NEW_LIST_ITEM,
            then: () => getCreateCampaignRemainingDailyBudgetValidation(remainingBudget)
          })
        })
  })
}

export const createCampaignPayload = ({ values, adAccountId, right, selectedPartnership }) => {
  const { allocation_type: rightAllocationType, bid_cap: rightBidCap } = right

  const isDisplayTypeCampaign = values[CAMPAIGN_TYPE] === DISPLAY_TYPE

  const getCampaignBudget = () => {
    if (rightAllocationType === DIGITAL_ALLOCATION_TYPE_IMPRESSIONS) {
      return calculateBudgetFromImpressions({
        impressions: values[IMPRESSIONS],
        rightBidCap
      })
    } else {
      return values[BUDGET_DAILY]
    }
  }

  const campaignName = formatCampaignName(values, selectedPartnership)

  return {
    account: adAccountId,
    name: campaignName,
    status: 'active',

    date_start: formatDateBE(values[START_DATE]),
    date_stop: formatDateBE(values[END_DATE]),
    [CAMPAIGN_BUDGET]: {
      delivery_method: 2,
      period: 2,
      type: 2,
      [NAME]: `Budget ${campaignName}`,
      [AMOUNT_MICROS]: getCampaignBudget(),
      explicitly_shared: false
    },
    // we create all display campaigns as manual_cpm bidding type by passing this field in payload
    ...(isDisplayTypeCampaign
      ? {
          manual_cpm: {}
        }
      : {
          [MAXIMIZE_CONVERSION_VALUE]: {
            [TARGET_ROAS]: !!values[TARGET_ROAS] ? values[TARGET_ROAS] : 0
          }
        }),
    ...defaultGoogleCampaignPayload(values)
  }
}
